import React, { createContext, useContext, useMemo } from "react";
import clsx from "clsx";
import Icon from "../Icon/Icon";
import { createPluggable } from "pluggable-react";
import Button from "../Button/Button";
import styles from "./Collapsible.module.scss";

export interface CollapsibleProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  defaultCollapsed?: boolean;
}

interface CollapsibleContextType {
  collapsed: boolean;
  toggleCollapsed: () => void;
}

const CollapsibleContext = createContext<CollapsibleContextType | null>(null);

export const useCollapsible = () => {
  const context = useContext(CollapsibleContext);
  if (!context) {
    throw new Error("useCollapsible must be used within a Collapsible");
  }
  return context;
};

const DefaultCollapsible = ({
  children,
  className,
  defaultCollapsed = true,
  ...props
}: CollapsibleProps) => {
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed);
  const toggleCollapsed = React.useCallback(() => setCollapsed(s => !s), []);

  const value = useMemo(() => ({ collapsed, toggleCollapsed }), [collapsed, toggleCollapsed]);

  return (
    <CollapsibleContext.Provider value={value}>
      <div className={clsx(styles.collapsible, className)} {...props}>
        {children}
      </div>
    </CollapsibleContext.Provider>
  );
};

const Collapsible = createPluggable<CollapsibleProps>("deci.collapsible", DefaultCollapsible);

export interface CollapsibleHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  onAdd?: () => void;
}

const DefaultCollapsibleHeader = ({ children, className, onAdd, ...props }: CollapsibleHeaderProps) => {
  const { collapsed, toggleCollapsed } = useCollapsible();
  return (
    <div className={clsx(styles.header, className)} data-collapsed={collapsed} onClick={toggleCollapsed} {...props}>
      <div className={styles.title}>{children}</div>
      <div className={styles.actions}>
        {onAdd && (
          <Button icon
            label="Add"
            onClick={(e) => {
              e.stopPropagation();
              onAdd();
            }}
            className={styles.addButton}
          >
            <Icon name="add" />
          </Button>
        )}
        <Icon
          name={collapsed ? "expand-section" : "collapse-section"}
          className={styles.collapseButton}
        />
      </div>
    </div>
  );
};

const CollapsibleHeader = createPluggable<CollapsibleHeaderProps>("deci.collapsible.header", DefaultCollapsibleHeader);

export interface CollapsibleContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const DefaultCollapsibleContent = ({ children, className, ...props }: CollapsibleContentProps) => {
  const { collapsed } = useCollapsible();

  if (collapsed) return null;

  return (
    <div className={clsx(styles.content, className)} {...props}>
      {children}
    </div>
  );
};

const CollapsibleContent = createPluggable<CollapsibleContentProps>("deci.collapsible.content", DefaultCollapsibleContent);

export default Object.assign(Collapsible, {
  Header: CollapsibleHeader,
  Content: CollapsibleContent,
});


